import React, { Component } from "react";
import {
  loading,
  alert,
  NavbarAdmin,
  Pagination,
  Modal,
  Select,
  DatePicker,
  GET,
  PUT,
  GET_FILE,
  saveAs,
  format_date,
  t,
} from "../../components/CustomComponent.js";
import "../../assets/css/warranty-expiry.css";

const PAGE_SIZE = 50;
const Q_DEBOUNCE_MS = 400;
const BUCKETS = ["exp", "d7", "d30", "d60", "d90"];
const FOLLOWUP_FILTERS = ["open", "all", "none", "contacted", "renewed", "not_renewed", "call_again"];
const FOLLOWUP_STATUSES = ["none", "contacted", "renewed", "not_renewed", "call_again"];
const SOURCE_TYPES = ["asset"];

const fill = (text, values) => Object.keys(values).reduce((acc, k) => acc.replace(`{${k}}`, values[k]), text);

// ตัด query param ที่ว่างออก
const pruneParams = (params) => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined));

// วันที่ตามภาษาปัจจุบัน (TH = พ.ศ., EN = ค.ศ.) - ค่าว่าง/ผิดรูปแบบ = "-"
const lang = () => (localStorage.getItem("lang") || "TH").toLowerCase();
const fmtDate = (value) => (value ? format_date(String(value).slice(0, 10), "dd/mm/yyyy", lang()) : "-");
const fmtDateTime = (value) => (value ? format_date(value, "dd/mm/yyyy hh:mm", lang()) : "-");

// สิทธิ์อ่านสดจาก localStorage (ไม่พึ่ง navbar re-render ที่ลบปุ่มตาม className) - super_user / admin-company ข้ามเหมือน PermissionRoute
const hasPermission = (key) => {
  try {
    const p = JSON.parse(localStorage.getItem("cm-profile"));
    if (["super_user", "admin-company"].includes(p?.user_type)) return true;
    return (p?.role?.permission || []).includes(key);
  } catch {
    return false;
  }
};

// วันนี้ (เวลาท้องถิ่น) รูปแบบ YYYY-MM-DD - ใช้เป็นวันต่ำสุดของ "โทรใหม่วันที่"
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// min ของ DatePicker เทียบแบบ strict กับ new Date("YYYY-MM-DD") (เที่ยงคืน UTC) -> ส่งเป็น "วันก่อนหน้า" (local) เพื่อให้เลือกวันนั้นได้
const dayBefore = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const x = new Date(y, m - 1, d - 1);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

// ปุ่มล้างตัวกรอง
const ClearFilterButton = ({ onClick, className = "" }) => (
  <button type="button" className={`btn btn-white border px-3 py-2 rounded-3 text-nowrap ${className}`.trim()} onClick={onClick} data-testid="wx-clear">
    {t("wx_clear_filters")}
  </button>
);

// หน้า "รายงานใกล้หมดประกัน" (Asset) - field ตรงกับ CMMS-Backend modules/admin/warranty-expiry
//   GET admin/warranty-expiry                         -> data={rows,kpi,meta} + pagination
//   GET admin/warranty-expiry/:source_type/:uid       -> row + history + alerts + followup
//   PUT admin/warranty-expiry/:source_type/:uid/followup -> {row, warnings}
//   GET admin/warranty-expiry/export                  -> xlsx (GET_FILE)
// ลิงก์จากอีเมล: ?bucket=<key>&open=<key> (key = <source_type>:<ref_uid>) -> เปิด Modal รายการนั้น
export default class AdminWarrantyExpiry extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(window.location.search);
    this.open_key = query.get("open") || "";
    const open = this.open_key.split(":");
    this.initial_open = SOURCE_TYPES.includes(open[0]) && open[1] ? { source_type: open[0], uid: open.slice(1).join(":") } : null;
    const bucket = query.get("bucket");
    this.canExport = hasPermission("warranty_expiry.read");
    this.canFollowup = hasPermission("warranty_expiry.read");
    this.q_timer = null;
    this.state = {
      filter_source_type: "",
      filter_followup: "open",
      filter_q: "",
      filter_bucket: BUCKETS.includes(bucket) ? bucket : "",
      sort_by: "days_left",
      sort_dir: "asc",

      rows: [],
      kpi: null,
      meta: null,
      count: 0,
      page: 1,
      loaded: false,

      detail: null, // รายการที่เปิด Modal
      form: null, // ฟอร์มบันทึกผลติดตามใน Modal
      form_error: "",
      warnings: [],
      saving: false,
    };
  }

  componentDidMount() {
    loading(false);
    this.Get(1).then(() => {
      const row = this.open_key ? this.state.rows.find((r) => r.key === this.open_key) : null;
      if (row) this.OpenDetail(row.source_type, row.ref_uid);
      else if (this.initial_open) this.OpenDetail(this.initial_open.source_type, this.initial_open.uid);
    });
  }

  componentWillUnmount() {
    clearTimeout(this.q_timer);
  }

  buildParams = () => {
    const { filter_source_type, filter_followup, filter_q, filter_bucket, sort_by, sort_dir } = this.state;
    return pruneParams({
      source_type: filter_source_type,
      followup_status: filter_followup,
      q: filter_q.trim(),
      bucket: filter_bucket,
      sort_by,
      sort_dir,
    });
  };

  Get = async (page = 1) => {
    loading(true);
    const result = await GET("admin/warranty-expiry", { ...this.buildParams(), page, limit: PAGE_SIZE });
    loading(false);
    if (result?.status) {
      const rows = result.data?.rows || [];
      this.setState({
        rows,
        kpi: result.data?.kpi || null,
        meta: result.data?.meta || null,
        count: result.pagination?.count ?? rows.length,
        page,
        loaded: true,
      });
      return;
    }
    this.setState({ rows: [], kpi: null, count: 0, page: 1, loaded: true });
  };

  SetFilter = (patch) => this.setState(patch, () => this.Get(1));

  SetQ = (value) => {
    this.setState({ filter_q: value });
    clearTimeout(this.q_timer);
    this.q_timer = setTimeout(() => this.Get(1), Q_DEBOUNCE_MS);
  };

  // KPI คลิกกรองช่วงนั้น คลิกซ้ำ = ยกเลิก
  ToggleBucket = (key) => this.SetFilter({ filter_bucket: this.state.filter_bucket === key ? "" : key });

  ResetFilter = () => {
    window.history.replaceState(null, "", window.location.pathname);
    this.SetFilter({ filter_source_type: "", filter_followup: "open", filter_q: "", filter_bucket: "" });
  };

  SortBy = (field) => {
    const { sort_by, sort_dir } = this.state;
    this.SetFilter({ sort_by: field, sort_dir: sort_by === field && sort_dir === "asc" ? "desc" : "asc" });
  };

  Export = async () => {
    loading(true);
    const result = await GET_FILE("admin/warranty-expiry/export", this.buildParams());
    loading(false);
    if (result?.status && result.blob) {
      saveAs(result.blob, result.fileName || "Warranty_Expiry.xlsx");
    } else {
      alert("", "warning", t("warning"), result?.message || t("wx_export_failed"));
    }
  };

  OpenDetail = async (source_type, uid) => {
    loading(true);
    const result = await GET(`admin/warranty-expiry/${source_type}/${uid}`);
    loading(false);
    if (!result?.status || !result.data) return;
    const fu = result.data.followup || {};
    this.setState({
      detail: result.data,
      form: { status: fu.status || "none", sap_doc_no: fu.sap_doc_no || "", note: fu.note || "", call_again_date: fu.call_again_date ? String(fu.call_again_date).slice(0, 10) : "" },
      form_error: "",
      warnings: [],
    });
  };

  CloseDetail = () => {
    const query = new URLSearchParams(window.location.search);
    if (query.has("open")) {
      query.delete("open");
      const qs = query.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    this.setState({ detail: null, form: null, warnings: [], form_error: "" });
  };

  originalCallAgainDate = (detail) => {
    const v = detail?.followup?.call_again_date;
    return v ? String(v).slice(0, 10) : "";
  };

  SaveFollowup = async () => {
    const { detail, form } = this.state;
    if (form.status === "not_renewed" && !form.note.trim()) {
      this.setState({ form_error: t("wx_fu_reason_required") });
      return;
    }
    if (form.status === "call_again") {
      if (!form.call_again_date) {
        this.setState({ form_error: t("wx_fu_call_again_date_required") });
        return;
      }
      // ค่าเดิมที่เก็บไว้ผ่านได้แม้เลยวันมาแล้ว (หลังบ้านตรวจเฉพาะเมื่อค่าเปลี่ยน)
      const original = this.originalCallAgainDate(detail);
      if (form.call_again_date !== original && form.call_again_date < todayIso()) {
        this.setState({ form_error: t("wx_fu_call_again_date_past") });
        return;
      }
    }
    this.setState({ saving: true, form_error: "" });
    loading(true);
    const result = await PUT(`admin/warranty-expiry/${detail.source_type}/${detail.ref_uid}/followup`, {
      status: form.status,
      sap_doc_no: form.sap_doc_no.trim(),
      note: form.note.trim(),
      // '-' = ล้างค่า (removeEmptyString ตัด '' ทิ้ง) ส่งเมื่อไม่ใช่ "ให้โทรใหม่"
      call_again_date: form.status === "call_again" ? form.call_again_date : "-",
    });
    loading(false);
    this.setState({ saving: false });
    if (!result?.status) {
      this.setState({ form_error: result?.message || t("wx_load_failed") });
      return;
    }
    const row = result.data?.row;
    this.setState({
      warnings: result.data?.warnings || [],
      detail: row
        ? { ...detail, followup: row.followup, followup_status: row.followup_status, last_alert: row.last_alert, followup_logs: result.data?.followup_logs || detail.followup_logs }
        : detail,
    });
    alert("", "success", t("success"), t("wx_fu_saved"));
    this.Get(this.state.page);
  };

  // ---------------------------------------------------------------- render
  bucketLabel = (key) => t(`wx_bucket_${key}`);

  renderDays = (row) => {
    const label = row.days_left < 0 ? fill(t("wx_expired_days"), { n: -row.days_left }) : fill(t("wx_days_left"), { n: row.days_left });
    return <span className={`d-inline-block text-12 fw-semibold px-2 py-1 rounded-pill text-nowrap wx-t-${row.bucket || "ok"}`}>{label}</span>;
  };

  renderFollowupText = (fu) => <span className={`fw-semibold wx-fu-${fu?.status || "none"}`}>{t(`wx_fu_${fu?.status || "none"}`)}</span>;

  renderKpi() {
    const { kpi, filter_bucket } = this.state;
    if (!kpi) return null;
    const card = (key, value, label, extra, onClick, testid) => (
      <div className="col-6 col-md-4 col-xl-2" key={key || "total"}>
        <button type="button" className={`wx2-kpi ${extra} rounded-4 shadow-sm p-3 h-100 ${filter_bucket === key ? "on" : ""}`} onClick={onClick} data-testid={testid}>
          <div className="text-secondary text-14 fw-bold mb-1" title={label}>{label}</div>
          <div className="v">{value}</div>
        </button>
      </div>
    );
    return (
      <div className="row g-3 mb-4" data-testid="wx-kpis">
        {card("", kpi.total, t("wx_kpi_total"), "total", () => this.SetFilter({ filter_bucket: "" }), "wx-kpi-total")}
        {BUCKETS.map((b) => card(b, kpi[b], this.bucketLabel(b), b, () => this.ToggleBucket(b), `wx-kpi-${b}`))}
      </div>
    );
  }

  renderSortTh = (field, label, className) => {
    const { sort_by, sort_dir } = this.state;
    return (
      <th className={`border-0 wx-th-sort ${className || ""}`} onClick={() => this.SortBy(field)}>
        {label} {sort_by === field ? (sort_dir === "asc" ? "▲" : "▼") : ""}
      </th>
    );
  };

  renderTable() {
    const { rows } = this.state;
    const th = (label, cls) => <th className={`border-0 ${cls || ""}`}>{label}</th>;
    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0 wx2-table">
          <thead>
            <tr className="text-dark fw-bold border-bottom border-light">
              {this.renderSortTh("days_left", t("wx_col_days"), "nw")}
              {this.renderSortTh("branch", t("wx_col_branch"), "nw")}
              {th(t("wx_col_type"), "nw")}
              {this.renderSortTh("ref_code", t("wx_col_ref"))}
              {this.renderSortTh("customer_name", t("wx_col_customer"))}
              {th(t("wx_col_item"))}
              {this.renderSortTh("expire_date", t("wx_col_expire"), "nw")}
              {th(t("wx_col_sales"), "nw")}
              {th(t("wx_col_last_alert"), "nw")}
              {th(t("wx_col_followup"), "nw")}
              <th className="border-0" />
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={10} className="text-center text-secondary py-5">
                  {t("wx_empty")}
                  <div><ClearFilterButton className="mt-2" onClick={this.ResetFilter} /></div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.key} className="border-bottom border-light bg-white pointer" onClick={() => this.OpenDetail(r.source_type, r.ref_uid)} data-testid="wx-row">
                  <td className="nw">{this.renderDays(r)}</td>
                  <td className="nw fw-bold text-dark">{r.branch_name || "-"}</td>
                  <td className="nw"><span className="d-inline-block text-12 fw-semibold px-2 py-1 rounded-pill bg-light text-dark">{t(`wx_src_${r.source_type}`)}</span></td>
                  <td className="ref">{r.ref_code}</td>
                  <td className="wrap text-dark">{r.customer_name || "-"}<div className="sub">{r.location || ""}</div></td>
                  <td className="wrap text-dark">{r.item_name || "-"}<div className="sub">{r.serial_or_size ? `${r.source_type === "asset" ? `${t("wx_serial")} ` : ""}${r.serial_or_size}` : ""}</div></td>
                  <td className="nw text-dark">{fmtDate(r.expire_date)}</td>
                  <td className="wrap text-dark" style={{ minWidth: 56 }}>{r.sales_name || "-"}</td>
                  <td className="nw sub">{r.last_alert?.label || "-"}</td>
                  <td style={{ minWidth: 64 }}>{this.renderFollowupText(r.followup)}</td>
                  <td className="nw">
                    <button type="button" className="btn btn-white border border-primary text-primary px-2 py-1 rounded-3 text-12" onClick={(e) => { e.stopPropagation(); this.OpenDetail(r.source_type, r.ref_uid); }}>{t("wx_detail")}</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  renderHistory(detail) {
    const items = detail.history?.items || [];
    return (
      <>
        <div className="text-14 fw-bold text-dark mb-2">{t("wx_d_history_asset")}</div>
        <div className="table-responsive border rounded-3 mb-4">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr className="text-dark fw-bold text-12 border-bottom border-light">
                <th className="py-2 border-0">{t("wx_h_no")}</th>
                <th className="py-2 border-0">{t("wx_h_start")}</th>
                <th className="py-2 border-0 text-end">{t("wx_h_months")}</th>
                <th className="py-2 border-0">{t("wx_h_end_asset")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h, i) => (
                <tr key={i} className="text-14 text-dark">
                  <td>
                    {h.no}
                    {h.used_for_calc ? <span className="text-12 fw-semibold text-primary bg-primary-light px-2 py-1 rounded-pill ms-1">{t("wx_h_used")}</span> : null}
                    {h.is_active === false ? <span className="text-12 fw-semibold text-secondary bg-light px-2 py-1 rounded-pill ms-1">{t("wx_h_inactive")}</span> : null}
                  </td>
                  <td>{fmtDate(h.start_date)}</td>
                  <td className="text-end">{h.months ?? "-"}</td>
                  <td>{fmtDate(h.end_date)}{h.remark ? <div className="text-secondary text-12">{h.remark}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  renderFollowupLogs(detail) {
    const logs = [...(detail.followup_logs || [])].sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
    return (
      <>
        <div className="text-14 fw-bold text-dark mb-2">{t("wx_log_title")} ({logs.length})</div>
        {!logs.length ? (
          <div className="border rounded-3 p-3 mb-4 text-secondary text-14" data-testid="wx-log-empty">{t("wx_log_empty")}</div>
        ) : (
          <div className="table-responsive border rounded-3 mb-4">
            <table className="table table-hover align-middle mb-0" data-testid="wx-log-table">
              <thead>
                <tr className="text-dark fw-bold text-12 border-bottom border-light">
                  <th className="py-2 border-0 text-nowrap">{t("wx_log_at")}</th>
                  <th className="py-2 border-0">{t("wx_log_by")}</th>
                  <th className="py-2 border-0">{t("wx_log_result")}</th>
                  <th className="py-2 border-0 text-nowrap">{t("wx_log_call_again")}</th>
                  <th className="py-2 border-0">{t("wx_log_note")}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} className="text-14 text-dark">
                    <td className="text-nowrap">{fmtDateTime(l.at)}</td>
                    <td>{l.by_name || "-"}</td>
                    <td>{l.result ? t(`wx_fu_${l.result}`) : "-"}</td>
                    <td className="text-nowrap">{fmtDate(l.call_again_date)}</td>
                    <td className="text-break">{l.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  renderFollowupForm() {
    const { detail, form, form_error, warnings, saving } = this.state;
    const fu = detail.followup || {};
    const original = this.originalCallAgainDate(detail);
    const setForm = (patch) => this.setState({ form: { ...form, ...patch }, form_error: "" });
    return (
      <>
        <div className="text-14 fw-bold text-dark mb-2">{t("wx_fu_title")}</div>
        <div className="border rounded-3 p-3 mb-4">
          {warnings.map((w) => (
            <div className="alert alert-warning py-2 text-14 fw-semibold" key={w.code} data-testid="wx-warning">{w.message}</div>
          ))}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label text-12 text-secondary mb-1">{t("wx_fu_status")}</label>
              <select className="form-select" value={form.status} disabled={!this.canFollowup} onChange={(e) => setForm({ status: e.target.value, ...(e.target.value === "call_again" ? {} : { call_again_date: "" }) })} data-testid="wx-fu-status">
                {FOLLOWUP_STATUSES.map((s) => <option key={s} value={s}>{t(`wx_fu_${s}`)}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label text-12 text-secondary mb-1">{t("wx_fu_sap")}</label>
              <input className="form-control" value={form.sap_doc_no} maxLength={100} disabled={!this.canFollowup} placeholder={t("wx_fu_sap_ph")} onChange={(e) => setForm({ sap_doc_no: e.target.value })} />
            </div>
          </div>
          {form.status === "call_again" ? (
            <div className="row g-3 mb-3">
              <div className="col-md-6" data-testid="wx-fu-call-again">
                <label className="form-label text-12 text-secondary mb-1">{t("wx_fu_call_again_date")}<span className="text-danger"> *</span></label>
                <DatePicker
                  type="th"
                  className="form-control"
                  min={dayBefore(original && original < todayIso() ? original : todayIso())}
                  value={form.call_again_date}
                  disabled={!this.canFollowup}
                  onChange={(val) => setForm({ call_again_date: val })}
                />
              </div>
            </div>
          ) : null}
          <label className="form-label text-12 text-secondary mb-1">{t("wx_fu_note")}{form.status === "not_renewed" ? <span className="text-danger"> *</span> : null}</label>
          <textarea className="form-control" rows={2} value={form.note} disabled={!this.canFollowup} placeholder={t("wx_fu_note_ph")} onChange={(e) => setForm({ note: e.target.value })} data-testid="wx-fu-note" />
          {form_error ? <div className="mt-1 text-danger text-12" data-testid="wx-fu-error">{form_error}</div> : null}
          {fu.updated_at ? (
            <div className="text-secondary text-12 mt-2">{t("wx_fu_last")} {fu.updated_by_name || "-"} · {fmtDateTime(fu.updated_at)}</div>
          ) : null}
          {!this.canFollowup ? <div className="text-secondary text-12 mt-2">{t("wx_fu_readonly")}</div> : null}
          <div className="d-flex justify-content-end flex-wrap gap-2 mt-3">
            {this.canFollowup ? <button type="button" className="btn btn-primary px-4 py-2 fw-bold rounded-3" disabled={saving} onClick={this.SaveFollowup} data-testid="wx-fu-save">{t("save")}</button> : null}
          </div>
        </div>
      </>
    );
  }

  renderDetailModal() {
    const { detail } = this.state;
    const bucket = detail?.bucket || "ok";
    const banner = !detail ? "" : detail.days_left < 0 ? fill(t("wx_d_banner_expired"), { n: -detail.days_left }) : fill(t("wx_d_banner_left"), { n: detail.days_left });
    const bannerColors = {
      exp: ["#ECEEF2", "#6B7280"], d7: ["#FDECEC", "#D64545"], d30: ["#FFEBDD", "#D9620B"], d60: ["#FFF4DC", "#B86E0A"], d90: ["#E8EDFF", "#2643B8"], ok: ["#E3F5EA", "#1F8A4C"],
    }[bucket];
    const alerts = detail?.alerts || [];
    const kv = detail
      ? [
          [t("wx_d_customer"), detail.customer_name],
          [t("wx_d_location"), detail.location],
          [t("wx_d_branch"), detail.branch_name],
          [t("wx_d_item_asset"), detail.item_name],
          [t("wx_d_serial_asset"), detail.serial_or_size],
          [t("wx_d_sales"), detail.sales_name],
        ]
      : [];

    return (
      <Modal show={!!detail} onHide={this.CloseDetail} centered size="lg" scrollable contentClassName="border-0 rounded-4 shadow-lg" data-testid="wx-drawer">
        <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton>
          {detail ? (
            <div>
              <div className="text-secondary text-12">{t(`wx_src_${detail.source_type}`)}{detail.branch_name ? ` · ${detail.branch_name}` : ""}</div>
              <h5 className="fw-bold mb-0 text-dark">{detail.ref_code}</h5>
            </div>
          ) : null}
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          {detail ? (
            <>
              <div className="rounded-3 p-3 mb-4 fw-semibold text-14" style={{ background: bannerColors[0], color: bannerColors[1] }}>
                {banner} · {t("wx_d_expire_asset")} {fmtDate(detail.expire_date)}
              </div>

              <div className="wx2-kv mb-4">
                {kv.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <div className="k">{label}</div>
                    <div className="text-dark text-break fw-semibold">{value || "-"}</div>
                  </React.Fragment>
                ))}
              </div>

              {this.renderHistory(detail)}
              {this.renderFollowupForm()}
              {this.renderFollowupLogs(detail)}

              <div className="text-14 fw-bold text-dark mb-2">{t("wx_alert_history")}</div>
              <div className="wx-timeline">
                {!alerts.length ? <div className="it text-secondary">{t("wx_alert_none")}</div> : null}
                {alerts.map((a, i) => (
                  <div className="it text-dark" key={i} style={a.is_current_cycle ? undefined : { opacity: 0.6 }}>
                    <b>{fmtDateTime(a.sent_at)}</b> — {fill(t("wx_alert_item"), { b: a.bucket })}
                    {a.is_current_cycle ? null : <span className="text-secondary text-12"> · {t("wx_alert_old_cycle")}</span>}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    );
  }

  render() {
    const { filter_followup, filter_q, meta, count, page, rows, loaded } = this.state;
    const dq = meta?.data_quality;
    const followupOptions = FOLLOWUP_FILTERS.map((f) => ({ value: f, label: f === "open" ? t("wx_f_followup_open") : f === "all" ? t("wx_f_followup_all") : t(`wx_fu_${f}`) }));

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("wx_title")}</h3>
              <div className="text-secondary text-14" data-testid="wx-as-of">
                {meta?.data_as_of || meta?.as_of ? `${t("wx_as_of")} ${meta.data_as_of ? fmtDateTime(meta.data_as_of) : fmtDate(meta.as_of)} · ` : ""}{t("wx_scope_hint")}
              </div>
            </div>
            {this.canExport ? (
              <button type="button" className="btn btn-success border-0 px-4 py-2 fw-bold rounded-3" onClick={this.Export} disabled={!rows.length} data-testid="wx-export-btn">{t("wx_export")}</button>
            ) : null}
          </div>

          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-3" data-testid="wx-f-followup">
                <label className="form-label text-12 text-secondary mb-1">{t("wx_f_followup")}</label>
                <Select
                  className="form-control w-100"
                  options={followupOptions}
                  sort={false}
                  value={filter_followup}
                  onChange={(v) => this.SetFilter({ filter_followup: v || "open" })}
                  placeholder={t("wx_f_followup_open")}
                />
              </div>
              <div className="col-md-5 col-lg-4">
                <label className="form-label text-12 text-secondary mb-1">{t("wx_f_search")}</label>
                <input type="text" className="form-control" value={filter_q} placeholder={t("wx_f_search_ph")} onChange={(e) => this.SetQ(e.target.value)} />
              </div>
              <div className="col-auto">
                <ClearFilterButton onClick={this.ResetFilter} />
              </div>
            </div>
          </div>

          <>
              {dq && dq.excluded_total > 0 ? (
                <div className="alert alert-warning text-14 py-2 mb-4" data-testid="wx-dq">
                  {fill(t("wx_dq"), { n: dq.excluded_total ?? 0, a: dq.missing_expire_date ?? 0 })}
                </div>
              ) : null}

              {this.renderKpi()}

              <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-4 pt-3 text-12 text-secondary">
                  <span>{fill(t("wx_shown"), { n: count })}</span>
                  <span>{t("wx_sort_hint")}</span>
                </div>
                {loaded ? this.renderTable() : null}
              </div>

              <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap text-secondary text-12">
                <div>
                  {t("showing")} {count ? (page - 1) * PAGE_SIZE + 1 : 0}-{(page - 1) * PAGE_SIZE + rows.length} {t("of")} {count}
                </div>
                <Pagination className="mb-0" count={Math.ceil(count / PAGE_SIZE) || 1} page={page} showFirstButton={false} showLastButton={false} onChange={(_, next) => this.Get(next)} />
              </div>
          </>
        </div>

        {this.renderDetailModal()}
      </div>
    );
  }
}
